import { type FormEvent, type KeyboardEvent, type ReactNode, useEffect, useState } from 'react'
import { NetworkStatus } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import {
  CalendarDays,
  Filter,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  X,
} from 'lucide-react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Button from '../components/ui/Button'
import {
  SEARCH_PAGE_SIZE,
  SEARCH_PROVIDERS_QUERY,
  SERVICE_CATEGORIES_QUERY,
  type SearchProviderSummary,
  type SearchProvidersData,
  type SearchProvidersInput,
  type SearchProvidersSortBy,
  type SearchProvidersVariables,
  type ServiceCategoriesData,
  type ServiceCategory,
  type SortOrder,
} from '../graphql/services'
import { useAuth } from '../contexts/AuthContext'
import { useLang, useScopedTranslations } from '../contexts/LangContext'
import { localized, normalizeLocale, type SupportedLocale } from '../lib/i18n'

type SortKey = 'rating' | 'price' | 'experience' | 'completedJobs'

interface SearchFilters {
  postalCode: string
  categoryId: string
  serviceTypeId: string
  date: string
  sortKey: SortKey
}

const defaultFilters: SearchFilters = {
  postalCode: '',
  categoryId: '',
  serviceTypeId: '',
  date: '',
  sortKey: 'rating',
}

const translations = {
  el: {
    pageEyebrow: 'Αναζήτηση',
    pageTitle: 'Βρες επαγγελματίες καθαρισμού κοντά σου',
    pageBody: 'Φίλτραρε ανά περιοχή, υπηρεσία και ημερομηνία για να βρεις τη σωστή επιλογή.',
    mobileFilters: 'Φίλτρα',
    filtersTitle: 'Φίλτρα αναζήτησης',
    filtersBody: 'Περιοχή, υπηρεσία, ημερομηνία και σειρά εμφάνισης.',
    postalCodeLabel: 'Ταχυδρομικός κώδικας',
    postalCodePlaceholder: 'π.χ. 11743',
    categoryLabel: 'Κατηγορία υπηρεσίας',
    categoryPlaceholder: 'Όλες οι κατηγορίες',
    serviceTypeLabel: 'Τύπος υπηρεσίας',
    serviceTypePlaceholder: 'Όλοι οι τύποι υπηρεσίας',
    serviceTypeHint: 'Επίλεξε συγκεκριμένο τύπο υπηρεσίας για να ενεργοποιηθεί η αναζήτηση της κατηγορίας.',
    dateLabel: 'Ημερομηνία',
    sortLabel: 'Ταξινόμηση',
    sortRating: 'Αξιολόγηση',
    sortPrice: 'Τιμή (χαμηλή → υψηλή)',
    sortExperience: 'Εμπειρία',
    sortCompletedJobs: 'Ολοκληρωμένες δουλειές',
    applyFilters: 'Εφαρμογή φίλτρων',
    clearFilters: 'Καθαρισμός φίλτρων',
    categoriesLoading: 'Φόρτωση κατηγοριών...',
    providersFound: 'πάροχοι βρέθηκαν',
    providerFound: 'πάροχος βρέθηκε',
    viewProfile: 'Προβολή προφίλ',
    verified: 'Επαληθευμένος',
    loadMore: 'Φόρτωση περισσότερων',
    loadingResults: 'Φορτώνουμε αποτελέσματα...',
    emptyTitle: 'Δεν βρέθηκαν επαγγελματίες',
    emptyBody: 'Δοκίμασε να αλλάξεις τα φίλτρα ή να αναζητήσεις άλλη περιοχή.',
    noPostalTitle: 'Συμπλήρωσε ταχυδρομικό κώδικα',
    noPostalBody: 'Χρησιμοποίησε 5ψήφιο ελληνικό ταχυδρομικό κώδικα για να δεις διαθέσιμους επαγγελματίες.',
    invalidPostalTitle: 'Μη έγκυρος ταχυδρομικός κώδικας',
    invalidPostalBody: 'Ο ταχυδρομικός κώδικας πρέπει να έχει ακριβώς 5 ψηφία.',
    chooseServiceTypeTitle: 'Διάλεξε τύπο υπηρεσίας',
    chooseServiceTypeBody: 'Η κατηγορία περιορίζει τις επιλογές. Επίλεξε συγκεκριμένο τύπο υπηρεσίας για να συνεχίσεις.',
    resultsError: 'Δεν ήταν δυνατή η φόρτωση των επαγγελματιών αυτή τη στιγμή.',
    resultsRetry: 'Δοκίμασε ξανά σε λίγο ή άλλαξε τα φίλτρα.',
    experienceYear: 'χρόνο εμπειρίας',
    experienceYears: 'χρόνια εμπειρίας',
    experienceUnavailable: 'Εμπειρία μη διαθέσιμη',
    pricingUnavailable: 'Τιμή κατόπιν συνεννόησης',
    servicesLabel: 'Υπηρεσίες',
    areasLabel: 'Εξυπηρετεί',
    moreAreas: 'ακόμη',
    reviews: 'κριτικές',
    completedJobs: 'ολοκληρωμένες δουλειές',
    viewHint: 'Άνοιγμα δημόσιου προφίλ',
    invalidPostalHint: 'Χρησιμοποίησε ακριβώς 5 ψηφία.',
  },
  en: {
    pageEyebrow: 'Search',
    pageTitle: 'Find cleaning providers near you',
    pageBody: 'Filter by area, service, and date to narrow in on the right fit.',
    mobileFilters: 'Filters',
    filtersTitle: 'Search filters',
    filtersBody: 'Area, service, date, and sort order.',
    postalCodeLabel: 'Postal code',
    postalCodePlaceholder: 'e.g. 11743',
    categoryLabel: 'Service category',
    categoryPlaceholder: 'All categories',
    serviceTypeLabel: 'Service type',
    serviceTypePlaceholder: 'All service types',
    serviceTypeHint: 'Choose a specific service type to search within this category.',
    dateLabel: 'Date',
    sortLabel: 'Sort by',
    sortRating: 'Rating',
    sortPrice: 'Price (low → high)',
    sortExperience: 'Experience',
    sortCompletedJobs: 'Completed jobs',
    applyFilters: 'Apply filters',
    clearFilters: 'Clear filters',
    categoriesLoading: 'Loading categories...',
    providersFound: 'providers found',
    providerFound: 'provider found',
    viewProfile: 'View Profile',
    verified: 'Verified',
    loadMore: 'Load More',
    loadingResults: 'Loading results...',
    emptyTitle: 'No providers found',
    emptyBody: 'Try adjusting your filters or searching another area.',
    noPostalTitle: 'Enter a postal code',
    noPostalBody: 'Use a 5-digit Greek postal code to see available providers.',
    invalidPostalTitle: 'Invalid postal code',
    invalidPostalBody: 'The postal code must contain exactly 5 digits.',
    chooseServiceTypeTitle: 'Choose a service type',
    chooseServiceTypeBody: 'The selected category narrows the options. Pick a specific service type to continue.',
    resultsError: 'We could not load providers right now.',
    resultsRetry: 'Please try again shortly or adjust the filters.',
    experienceYear: 'year experience',
    experienceYears: 'years experience',
    experienceUnavailable: 'Experience unavailable',
    pricingUnavailable: 'Price on request',
    servicesLabel: 'Services',
    areasLabel: 'Serves',
    moreAreas: 'more',
    reviews: 'reviews',
    completedJobs: 'completed jobs',
    viewHint: 'Open public profile',
    invalidPostalHint: 'Use exactly 5 digits.',
  },
} as const

type SearchCopy = Record<keyof typeof translations.en, string>

const sortOptions: Array<{
  key: SortKey
  sortBy: SearchProvidersSortBy
  sortOrder: SortOrder
  label: { el: string; en: string }
}> = [
  {
    key: 'rating',
    sortBy: 'RATING',
    sortOrder: 'DESC',
    label: { el: translations.el.sortRating, en: translations.en.sortRating },
  },
  {
    key: 'price',
    sortBy: 'PRICE',
    sortOrder: 'ASC',
    label: { el: translations.el.sortPrice, en: translations.en.sortPrice },
  },
  {
    key: 'experience',
    sortBy: 'EXPERIENCE',
    sortOrder: 'DESC',
    label: { el: translations.el.sortExperience, en: translations.en.sortExperience },
  },
  {
    key: 'completedJobs',
    sortBy: 'COMPLETED_JOBS',
    sortOrder: 'DESC',
    label: { el: translations.el.sortCompletedJobs, en: translations.en.sortCompletedJobs },
  },
]

function normalizeSortKey(value: string | null): SortKey {
  return sortOptions.some((option) => option.key === value) ? (value as SortKey) : 'rating'
}

function buildSearchParams(filters: SearchFilters) {
  const params = new URLSearchParams()

  if (filters.postalCode) params.set('postalCode', filters.postalCode)
  if (filters.categoryId) params.set('category', filters.categoryId)
  if (filters.serviceTypeId) params.set('serviceType', filters.serviceTypeId)
  if (filters.date) params.set('date', filters.date)
  if (filters.sortKey !== defaultFilters.sortKey) params.set('sort', filters.sortKey)

  return params
}

function parseFilters(searchParams: URLSearchParams): SearchFilters {
  return {
    postalCode: (searchParams.get('postalCode') ?? '').replace(/\D/g, '').slice(0, 5),
    categoryId: searchParams.get('category') ?? '',
    serviceTypeId: searchParams.get('serviceType') ?? '',
    date: searchParams.get('date') ?? '',
    sortKey: normalizeSortKey(searchParams.get('sort')),
  }
}

function findCategoryForServiceType(serviceTypeId: string, categories: ServiceCategory[]) {
  if (!serviceTypeId) return null

  return categories.find((category) => category.serviceTypes.some((serviceType) => serviceType.id === serviceTypeId)) ?? null
}

function getSortOption(sortKey: SortKey) {
  return sortOptions.find((option) => option.key === sortKey) ?? sortOptions[0]
}

function getNextPage(endCursor: string | null) {
  if (!endCursor) return null

  const offset = Number(endCursor)
  if (Number.isNaN(offset)) return null

  return Math.floor(offset / SEARCH_PAGE_SIZE) + 1
}

function providerCountLabel(count: number, copy: SearchCopy) {
  if (count === 1) return `1 ${copy.providerFound}`
  return `${count} ${copy.providersFound}`
}

function formatDisplayName(provider: SearchProviderSummary) {
  const lastInitial = provider.user.lastName.charAt(0)
  return lastInitial ? `${provider.user.firstName} ${lastInitial}.` : provider.user.firstName
}

function formatExperience(yearsExperience: number | null, copy: SearchCopy) {
  if (yearsExperience === null) return copy.experienceUnavailable
  return `${yearsExperience} ${yearsExperience === 1 ? copy.experienceYear : copy.experienceYears}`
}

function formatPrice(hourlyRateCents: number | null, locale: SupportedLocale, copy: SearchCopy) {
  if (hourlyRateCents === null) return copy.pricingUnavailable

  const currencyLocale = locale === 'el' ? 'el-GR' : 'en-GB'
  const amount = new Intl.NumberFormat(currencyLocale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(hourlyRateCents / 100)

  return `${amount}/${locale === 'el' ? 'ώρα' : 'hr'}`
}

function formatAreas(provider: SearchProviderSummary, copy: SearchCopy) {
  const areaNames = Array.from(
    new Set(
      provider.serviceAreas
        .map((area) => area.areaName || area.city)
        .filter((value): value is string => Boolean(value)),
    ),
  )

  if (areaNames.length <= 3) return areaNames.join(', ')

  const remaining = areaNames.length - 3
  return `${areaNames.slice(0, 3).join(', ')} +${remaining} ${copy.moreAreas}`
}

function baseFieldClass(disabled = false) {
  return [
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100',
    disabled ? 'cursor-not-allowed bg-slate-50 text-slate-400' : 'hover:border-slate-300',
  ].join(' ')
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <label className="mb-2 block text-sm font-semibold text-slate-700">{children}</label>
}

interface FiltersPanelProps {
  categories: ServiceCategory[]
  categoriesLoading: boolean
  copy: SearchCopy
  filters: SearchFilters
  locale: SupportedLocale
  onApply: (event: FormEvent<HTMLFormElement>) => void
  onCategoryChange: (categoryId: string) => void
  onClear: () => void
  onClose?: () => void
  onDateChange: (value: string) => void
  onPostalCodeChange: (value: string) => void
  onServiceTypeChange: (serviceTypeId: string) => void
  onSortChange: (sortKey: SortKey) => void
  postalCodeValid: boolean
}

function FiltersPanel({
  categories,
  categoriesLoading,
  copy,
  filters,
  locale,
  onApply,
  onCategoryChange,
  onClear,
  onClose,
  onDateChange,
  onPostalCodeChange,
  onServiceTypeChange,
  onSortChange,
  postalCodeValid,
}: FiltersPanelProps) {
  const inferredCategory = findCategoryForServiceType(filters.serviceTypeId, categories)
  const selectedCategoryId = filters.categoryId || inferredCategory?.id || ''
  const activeCategory = categories.find((category) => category.id === selectedCategoryId) ?? null
  const serviceTypes = activeCategory ? activeCategory.serviceTypes : categories.flatMap((category) => category.serviceTypes)

  return (
    <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-950">{copy.filtersTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">{copy.filtersBody}</p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <form onSubmit={onApply} className="mt-6 space-y-5">
        <div>
          <FieldLabel>{copy.postalCodeLabel}</FieldLabel>
          <input
            type="text"
            inputMode="numeric"
            value={filters.postalCode}
            onChange={(event) => onPostalCodeChange(event.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder={copy.postalCodePlaceholder}
            className={baseFieldClass()}
          />
          {!postalCodeValid && (
            <p className="mt-2 text-xs font-medium text-red-600">{copy.invalidPostalHint}</p>
          )}
        </div>

        <div>
          <FieldLabel>{copy.categoryLabel}</FieldLabel>
          <select
            value={selectedCategoryId}
            onChange={(event) => onCategoryChange(event.target.value)}
            className={baseFieldClass(categoriesLoading)}
            disabled={categoriesLoading}
          >
            <option value="">{categoriesLoading ? copy.categoriesLoading : copy.categoryPlaceholder}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {localized(category, locale)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <FieldLabel>{copy.serviceTypeLabel}</FieldLabel>
          <select
            value={filters.serviceTypeId}
            onChange={(event) => onServiceTypeChange(event.target.value)}
            className={baseFieldClass(categoriesLoading)}
            disabled={categoriesLoading}
          >
            <option value="">{categoriesLoading ? copy.categoriesLoading : copy.serviceTypePlaceholder}</option>
            {serviceTypes.map((serviceType) => (
              <option key={serviceType.id} value={serviceType.id}>
                {localized(serviceType, locale)}
              </option>
            ))}
          </select>
          {filters.categoryId && !filters.serviceTypeId && (
            <p className="mt-2 text-xs font-medium text-slate-500">{copy.serviceTypeHint}</p>
          )}
        </div>

        <div>
          <FieldLabel>{copy.dateLabel}</FieldLabel>
          <div className="relative">
            <input
              type="date"
              value={filters.date}
              onChange={(event) => onDateChange(event.target.value)}
              className={baseFieldClass()}
            />
            <CalendarDays className="pointer-events-none absolute right-4 top-3.5 h-4 w-4 text-slate-400" />
          </div>
        </div>

        <div>
          <FieldLabel>{copy.sortLabel}</FieldLabel>
          <select
            value={filters.sortKey}
            onChange={(event) => onSortChange(normalizeSortKey(event.target.value))}
            className={baseFieldClass()}
          >
            {sortOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label[locale]}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-3 pt-1">
          <Button type="submit" fullWidth size="lg" disabled={!postalCodeValid}>
            {copy.applyFilters}
          </Button>
          <button
            type="button"
            onClick={onClear}
            className="w-full text-center text-sm font-semibold text-slate-500 transition hover:text-primary-600"
          >
            {copy.clearFilters}
          </button>
        </div>
      </form>
    </div>
  )
}

function SearchSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 animate-pulse rounded-full bg-slate-100" />
            <div className="flex-1">
              <div className="h-5 w-40 animate-pulse rounded-full bg-slate-100" />
              <div className="mt-3 h-4 w-52 animate-pulse rounded-full bg-slate-100" />
              <div className="mt-6 space-y-2">
                <div className="h-4 animate-pulse rounded-full bg-slate-100" />
                <div className="h-4 w-5/6 animate-pulse rounded-full bg-slate-100" />
              </div>
            </div>
          </div>
          <div className="mt-6 h-10 animate-pulse rounded-2xl bg-slate-100" />
        </div>
      ))}
    </div>
  )
}

interface ProviderCardProps {
  copy: SearchCopy
  locale: SupportedLocale
  provider: SearchProviderSummary
}

function ProviderCard({ copy, locale, provider }: ProviderCardProps) {
  const navigate = useNavigate()
  const providerPath = `/providers/${provider.id}`

  function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
    if (event.key !== 'Enter' && event.key !== ' ') return

    event.preventDefault()
    navigate(providerPath)
  }

  return (
    <article
      role="link"
      tabIndex={0}
      data-cy="search-provider-card"
      onClick={() => navigate(providerPath)}
      onKeyDown={handleKeyDown}
      className="cursor-pointer rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:border-primary-200 hover:shadow-[0_20px_45px_-28px_rgba(37,99,235,0.35)] focus:outline-none focus:ring-4 focus:ring-primary-100"
      aria-label={`${copy.viewHint}: ${formatDisplayName(provider)}`}
    >
      <div className="flex items-start gap-4">
        {provider.user.avatarUrl ? (
          <img
            src={provider.user.avatarUrl}
            alt=""
            className="h-14 w-14 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
            {provider.user.firstName.charAt(0)}
            {provider.user.lastName.charAt(0)}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-bold text-slate-950" data-cy="search-provider-name">{formatDisplayName(provider)}</h3>
              <p className="mt-2 text-sm font-medium text-slate-500">
                {formatExperience(provider.yearsExperience, copy)}
              </p>
            </div>

            <div className="rounded-full bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-700">
              <span className="inline-flex items-center gap-1.5">
                <Star className="h-4 w-4 fill-current" />
                {provider.averageRating.toFixed(1)} ({provider.totalReviews} {copy.reviews})
              </span>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            {provider.verified && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-emerald-700">
                <ShieldCheck className="h-4 w-4" />
                {copy.verified}
              </span>
            )}
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-slate-600">
              {provider.completedJobs} {copy.completedJobs}
            </span>
          </div>

          {provider.bio && (
            <p className="mt-4 text-sm leading-7 text-slate-600">
              {provider.bio}
            </p>
          )}

          <div className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-900">{copy.servicesLabel}:</span>{' '}
              {provider.serviceTypes.map((serviceType) => localized(serviceType, locale)).join(', ')}
            </p>
            <p>
              <span className="font-semibold text-slate-900">{copy.areasLabel}:</span>{' '}
              {formatAreas(provider, copy)}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-5">
            <p className="text-lg font-bold text-primary-700">
              {formatPrice(provider.hourlyRateCents, locale, copy)}
            </p>
            <Link
              to={providerPath}
              onClick={(event) => event.stopPropagation()}
              className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700"
            >
              {copy.viewProfile}
            </Link>
          </div>
        </div>
      </div>
    </article>
  )
}

function ResultsMessage({
  title,
  body,
  tone = 'neutral',
}: {
  title: string
  body: string
  tone?: 'neutral' | 'error'
}) {
  const toneClasses = tone === 'error'
    ? 'border-red-100 bg-red-50 text-red-700'
    : 'border-slate-200 bg-white text-slate-700'

  return (
    <div className={`rounded-[1.75rem] border px-6 py-8 shadow-sm ${toneClasses}`}>
      <h3 className="text-xl font-bold">{title}</h3>
      <p className="mt-3 max-w-2xl text-sm leading-7">{body}</p>
    </div>
  )
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams()
  const appliedSearchKey = searchParams.toString()
  const appliedFilters = parseFilters(searchParams)

  const { currentUser } = useAuth()
  const { locale: appLocale } = useLang()
  const activeLocale = normalizeLocale(currentUser?.locale ?? appLocale)
  const copy = useScopedTranslations('search', translations[activeLocale], activeLocale)

  const [draftFilters, setDraftFilters] = useState<SearchFilters>(appliedFilters)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  const {
    data: categoryData,
    loading: categoriesLoading,
  } = useQuery<ServiceCategoriesData>(SERVICE_CATEGORIES_QUERY)

  const categories = categoryData?.serviceCategories ?? []
  const appliedSortOption = getSortOption(appliedFilters.sortKey)
  const requiresServiceTypeSelection = Boolean(appliedFilters.categoryId) && !appliedFilters.serviceTypeId
  const hasPostalCode = appliedFilters.postalCode.length > 0
  const hasValidPostalCode = appliedFilters.postalCode.length === 5
  const postalCodeValid = draftFilters.postalCode.length === 0 || draftFilters.postalCode.length === 5

  const searchInput: SearchProvidersInput = {
    postalCode: appliedFilters.postalCode,
    date: appliedFilters.date || undefined,
    page: 1,
    perPage: SEARCH_PAGE_SIZE,
    sortBy: appliedSortOption.sortBy,
    sortOrder: appliedSortOption.sortOrder,
  }

  if (appliedFilters.serviceTypeId) {
    searchInput.serviceTypeId = appliedFilters.serviceTypeId
  }

  const {
    data,
    error,
    fetchMore,
    loading,
    networkStatus,
  } = useQuery<SearchProvidersData, SearchProvidersVariables>(SEARCH_PROVIDERS_QUERY, {
    variables: { input: searchInput },
    skip: !hasValidPostalCode || requiresServiceTypeSelection,
    notifyOnNetworkStatusChange: true,
  })

  useEffect(() => {
    setDraftFilters(parseFilters(searchParams))
  }, [appliedSearchKey, searchParams])

  const searchResults = data?.searchProviders.providers ?? []
  const pageInfo = data?.searchProviders.pageInfo
  const initialLoading = loading && networkStatus !== NetworkStatus.fetchMore
  const loadingMore = networkStatus === NetworkStatus.fetchMore

  function commitFilters(nextFilters: SearchFilters) {
    setSearchParams(buildSearchParams(nextFilters))
  }

  function handleApplyFilters(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!postalCodeValid) return

    commitFilters(draftFilters)
    setMobileFiltersOpen(false)
  }

  function handleClearFilters() {
    setDraftFilters(defaultFilters)
    setSearchParams(new URLSearchParams())
    setMobileFiltersOpen(false)
  }

  function handleCategoryChange(categoryId: string) {
    const selectedCategory = categories.find((category) => category.id === categoryId) ?? null
    const nextServiceTypeId = selectedCategory?.serviceTypes.some((serviceType) => serviceType.id === draftFilters.serviceTypeId)
      ? draftFilters.serviceTypeId
      : ''

    setDraftFilters((current) => ({
      ...current,
      categoryId,
      serviceTypeId: nextServiceTypeId,
    }))
  }

  function handleHeaderSortChange(sortKey: SortKey) {
    const nextFilters = {
      ...appliedFilters,
      sortKey,
    }

    setDraftFilters(nextFilters)
    commitFilters(nextFilters)
  }

  async function handleLoadMore() {
    const nextPage = getNextPage(pageInfo?.endCursor ?? null)
    if (!nextPage) return

    await fetchMore({
      variables: {
        input: {
          ...searchInput,
          page: nextPage,
        },
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous

        const existingIds = new Set(previous.searchProviders.providers.map((provider) => provider.id))
        const incomingProviders = fetchMoreResult.searchProviders.providers.filter((provider) => !existingIds.has(provider.id))

        return {
          searchProviders: {
            ...fetchMoreResult.searchProviders,
            providers: [
              ...previous.searchProviders.providers,
              ...incomingProviders,
            ],
          },
        }
      },
    })
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-primary-600">{copy.pageEyebrow}</p>
          <h1 className="mt-3 text-4xl font-black tracking-tight text-slate-950 sm:text-5xl">
            {copy.pageTitle}
          </h1>
          <p className="mt-4 text-lg leading-8 text-slate-600">{copy.pageBody}</p>
        </div>

        <button
          type="button"
          onClick={() => setMobileFiltersOpen(true)}
          className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-primary-200 hover:text-primary-700 lg:hidden"
        >
          <Filter className="h-4 w-4" />
          {copy.mobileFilters}
        </button>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)] lg:items-start">
        <aside className="hidden lg:block lg:sticky lg:top-24">
          <FiltersPanel
            categories={categories}
            categoriesLoading={categoriesLoading}
            copy={copy}
            filters={draftFilters}
            locale={activeLocale}
            onApply={handleApplyFilters}
            onCategoryChange={handleCategoryChange}
            onClear={handleClearFilters}
            onDateChange={(value) => setDraftFilters((current) => ({ ...current, date: value }))}
            onPostalCodeChange={(value) => setDraftFilters((current) => ({ ...current, postalCode: value }))}
            onServiceTypeChange={(serviceTypeId) => setDraftFilters((current) => ({ ...current, serviceTypeId }))}
            onSortChange={(sortKey) => setDraftFilters((current) => ({ ...current, sortKey }))}
            postalCodeValid={postalCodeValid}
          />
        </aside>

        <section className="min-w-0">
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
                  <span data-cy="search-results-count">
                    {hasValidPostalCode && !requiresServiceTypeSelection && !initialLoading && !error
                      ? providerCountLabel(data?.searchProviders.totalCount ?? 0, copy)
                      : copy.pageEyebrow}
                  </span>
                </p>
                <h2 className="mt-2 text-2xl font-bold text-slate-950">{copy.pageTitle}</h2>
              </div>

              <div className="sm:w-64">
                <label htmlFor="search-sort-header" className="mb-2 block text-sm font-semibold text-slate-700">
                  {copy.sortLabel}
                </label>
                <div className="relative">
                  <select
                    id="search-sort-header"
                    value={appliedFilters.sortKey}
                    onChange={(event) => handleHeaderSortChange(normalizeSortKey(event.target.value))}
                    className={baseFieldClass()}
                  >
                    {sortOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label[activeLocale]}
                      </option>
                    ))}
                  </select>
                  <SlidersHorizontal className="pointer-events-none absolute right-4 top-3.5 h-4 w-4 text-slate-400" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {!hasPostalCode && (
              <ResultsMessage title={copy.noPostalTitle} body={copy.noPostalBody} />
            )}

            {hasPostalCode && !hasValidPostalCode && (
              <ResultsMessage title={copy.invalidPostalTitle} body={copy.invalidPostalBody} />
            )}

            {hasValidPostalCode && requiresServiceTypeSelection && (
              <ResultsMessage title={copy.chooseServiceTypeTitle} body={copy.chooseServiceTypeBody} />
            )}

            {hasValidPostalCode && !requiresServiceTypeSelection && initialLoading && (
              <SearchSkeleton />
            )}

            {hasValidPostalCode && !requiresServiceTypeSelection && !initialLoading && error && (
              <ResultsMessage title={copy.resultsError} body={copy.resultsRetry} tone="error" />
            )}

            {hasValidPostalCode && !requiresServiceTypeSelection && !initialLoading && !error && searchResults.length === 0 && (
              <ResultsMessage title={copy.emptyTitle} body={copy.emptyBody} />
            )}

            {hasValidPostalCode && !requiresServiceTypeSelection && !initialLoading && !error && searchResults.length > 0 && (
              <div className="space-y-4">
                {searchResults.map((provider) => (
                  <ProviderCard key={provider.id} copy={copy} locale={activeLocale} provider={provider} />
                ))}

                {pageInfo?.hasNextPage && (
                  <div className="pt-2">
                    <Button
                      type="button"
                      size="lg"
                      loading={loadingMore}
                      onClick={handleLoadMore}
                      className="w-full rounded-2xl"
                    >
                      {copy.loadMore}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 flex items-end bg-slate-950/40 p-3 lg:hidden">
          <div className="max-h-[92vh] w-full overflow-y-auto rounded-[2rem] bg-slate-50 p-1 shadow-2xl">
            <FiltersPanel
              categories={categories}
              categoriesLoading={categoriesLoading}
              copy={copy}
              filters={draftFilters}
              locale={activeLocale}
              onApply={handleApplyFilters}
              onCategoryChange={handleCategoryChange}
              onClear={handleClearFilters}
              onClose={() => setMobileFiltersOpen(false)}
              onDateChange={(value) => setDraftFilters((current) => ({ ...current, date: value }))}
              onPostalCodeChange={(value) => setDraftFilters((current) => ({ ...current, postalCode: value }))}
              onServiceTypeChange={(serviceTypeId) => setDraftFilters((current) => ({ ...current, serviceTypeId }))}
              onSortChange={(sortKey) => setDraftFilters((current) => ({ ...current, sortKey }))}
              postalCodeValid={postalCodeValid}
            />
          </div>
        </div>
      )}
    </div>
  )
}
